import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useWebSocket } from '../WebSocketContext';

const DEFAULT_LOOP_TAG_NAME = 'loopTag';

const parseFlowId = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const normalizeSourceTagValue = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }

  const escapedMatch = normalized.match(/^\{\{([a-zA-Z0-9_]+)\}\}$/);
  if (escapedMatch) {
    return escapedMatch[1];
  }

  const directMatch = normalized.match(/^\{([a-zA-Z0-9_]+)\}$/);
  if (directMatch) {
    return directMatch[1];
  }

  return normalized;
};

const normalizeLoopTagName = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }

  const escapedMatch = normalized.match(/^\{\{([a-zA-Z0-9_]+)\}\}$/);
  if (escapedMatch) {
    return escapedMatch[1];
  }

  const directMatch = normalized.match(/^\{([a-zA-Z0-9_]+)\}$/);
  if (directMatch) {
    return directMatch[1];
  }

  return normalized;
};

const isValidLoopTagName = (value) => /^[a-zA-Z0-9_]+$/.test(String(value || '').trim());

const ActionsLoopTriggerFlow = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  workflowFlows = [],
  currentFlowId = null
}) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  const [localFlows, setLocalFlows] = useState([]);
  const [targetFlowId, setTargetFlowId] = useState('');
  const [sourceTagInput, setSourceTagInput] = useState('');
  const [instruction, setInstruction] = useState('');
  const [loopTagName, setLoopTagName] = useState(DEFAULT_LOOP_TAG_NAME);

  const sourceFlowId = useMemo(() => parseFlowId(currentFlowId), [currentFlowId]);

  useEffect(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !caseid) {
      return;
    }
    ws.send(JSON.stringify({ command: 'getWorkflowFlows', caseid }));
  }, [ws, caseid]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      if (parsedMessage?.command !== 'getWorkflowFlowsResponse') {
        return;
      }
      const payload = parsedMessage?.data || {};
      if (String(payload.caseid) !== String(caseid)) {
        return;
      }
      const flows = Array.isArray(payload.flows) ? payload.flows : [];
      setLocalFlows(flows);
    });
    return unsubscribe;
  }, [subscribeToMessages, caseid]);

  const selectableFlows = useMemo(() => {
    const source = Array.isArray(workflowFlows) && workflowFlows.length > 0 ? workflowFlows : localFlows;
    return source
      .map((flow) => {
        const id = parseFlowId(flow?.id);
        if (!id) {
          return null;
        }
        return {
          id,
          name: String(flow?.name || `Flow ${id}`),
          orderIndex: Number(flow?.orderIndex || 0)
        };
      })
      .filter((flow) => flow && flow.id !== sourceFlowId)
      .sort((a, b) => {
        if (a.orderIndex !== b.orderIndex) {
          return a.orderIndex - b.orderIndex;
        }
        return a.id - b.id;
      });
  }, [workflowFlows, localFlows, sourceFlowId]);

  useEffect(() => {
    if (parseFlowId(targetFlowId)) {
      return;
    }
    if (selectableFlows.length > 0) {
      setTargetFlowId(String(selectableFlows[0].id));
    }
  }, [targetFlowId, selectableFlows]);

  const selectedTargetFlowId = parseFlowId(targetFlowId);
  const selectedTargetExists = selectableFlows.some((flow) => flow.id === selectedTargetFlowId);
  const isTargetMissing = !selectedTargetFlowId || !selectedTargetExists;
  const normalizedSourceTag = normalizeSourceTagValue(sourceTagInput);
  const normalizedLoopTagName = normalizeLoopTagName(loopTagName);
  const isLoopTagNameInvalid = !isValidLoopTagName(normalizedLoopTagName);
  const isLoopTagNameConflicting = normalizedLoopTagName === normalizedSourceTag;
  const isConfigurationIncomplete = (
    !normalizedSourceTag ||
    !instruction.trim() ||
    !normalizedLoopTagName ||
    isLoopTagNameInvalid ||
    isLoopTagNameConflicting ||
    isTargetMissing
  );

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'looptriggerflow',
    onSaveStatusChange,
    customComparison: (initial) => {
      const initialTarget = parseFlowId(initial.targetFlowId || initial.target_flow_id);
      const currentTarget = parseFlowId(targetFlowId);
      return (
        initialTarget === currentTarget &&
        normalizedSourceTag === normalizeSourceTagValue(initial.sourceTag || initial.source_tag || '') &&
        instruction === (initial.instruction || '') &&
        normalizedLoopTagName === normalizeLoopTagName(initial.loopTagName || initial.loop_tag_name || DEFAULT_LOOP_TAG_NAME)
      );
    },
    customSaveData: () => ({
      targetFlowId: parseFlowId(targetFlowId),
      sourceTag: normalizedSourceTag,
      instruction,
      loopTagName: normalizedLoopTagName,
      useConditions: true,
      conditions
    }),
    loadDataFunction: (data) => {
      const savedTarget = parseFlowId(
        data?.customData?.targetFlowId || data?.customData?.target_flow_id
      );
      setTargetFlowId(savedTarget ? String(savedTarget) : '');
      setSourceTagInput(
        (() => {
          const normalizedSource = normalizeSourceTagValue(
            data?.customData?.sourceTag || data?.customData?.source_tag || ''
          );
          return normalizedSource ? `{${normalizedSource}}` : '';
        })()
      );
      setInstruction(data?.customData?.instruction || '');
      setLoopTagName(
        normalizeLoopTagName(data?.customData?.loopTagName || data?.customData?.loop_tag_name || DEFAULT_LOOP_TAG_NAME)
        || DEFAULT_LOOP_TAG_NAME
      );
    }
  });

  return (
    <ActionSettingsLayout
      title={t('workflow.looptriggerflow', 'Search and Act')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled || isConfigurationIncomplete}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Card
        sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #5757b010',
          overflow: 'hidden'
        }}
      >
        <CardHeader
          title={
            <Typography
              sx={{
                fontWeight: 700,
                color: '#444',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Box
                component="span"
                sx={{
                  bgcolor: '#5757b0',
                  color: 'white',
                  borderRadius: '6px',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)'
                }}
              >
                <AutorenewIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                LOOP
              </Box>
              {t('workflow.looptriggerflow.settings_title', 'Loop Configuration')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 }
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Typography sx={{ mb: 2, fontSize: '0.9rem', color: '#666' }}>
            {t(
              'workflow.looptriggerflow.description',
              'Split one tag into multiple loop items and run another flow once for each item.'
            )}
          </Typography>

          <Typography sx={{ fontWeight: 600, color: '#444', fontSize: '0.9rem', mb: 1 }}>
            {t('workflow.looptriggerflow.source_tag', 'Where to search?')}
          </Typography>
          <Box
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              minHeight: '64px',
              mb: 2
            }}
          >
            <TagEditor
              caseid={caseid}
              value={sourceTagInput}
              onChange={setSourceTagInput}
              tags={tags}
              placeholder={t('workflow.looptriggerflow.select_source_tag', 'Select a tag')}
              singleLine
            />
          </Box>

          <Typography sx={{ fontWeight: 600, color: '#444', fontSize: '0.9rem', mb: 1 }}>
            {t('workflow.looptriggerflow.instruction', 'What to search?')}
          </Typography>
          <Box
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              minHeight: '110px',
              mb: 2
            }}
          >
            <TagEditor
              caseid={caseid}
              value={instruction}
              onChange={setInstruction}
              tags={tags}
              placeholder={t(
                'workflow.looptriggerflow.instruction_placeholder',
                'Example: For each animal in this tag, return one clean item.'
              )}
              multiline
              minRows={3}
            />
          </Box>

          <TextField
            fullWidth
            size="small"
            label={t('workflow.looptriggerflow.loop_tag_name', 'Loop Tag Name')}
            value={loopTagName}
            onChange={(event) => setLoopTagName(normalizeLoopTagName(event.target.value))}
            placeholder={t('workflow.looptriggerflow.loop_tag_name_placeholder', 'Example: animalItem')}
            error={isLoopTagNameInvalid || isLoopTagNameConflicting}
            helperText={
              isLoopTagNameConflicting
                ? t(
                    'workflow.looptriggerflow.loop_tag_name_conflict',
                    'The temporary tag name must be different from the source tag.'
                  )
                : t(
                    'workflow.looptriggerflow.loop_tag_name_help',
                    'Letters, numbers and underscores only.'
                  )
            }
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth size="small">
            <InputLabel id="looptriggerflow-target-label">
              {t('workflow.looptriggerflow.target_flow', 'Target Flow')}
            </InputLabel>
            <Select
              labelId="looptriggerflow-target-label"
              value={selectedTargetExists ? String(selectedTargetFlowId) : ''}
              label={t('workflow.looptriggerflow.target_flow', 'Target Flow')}
              onChange={(event) => setTargetFlowId(event.target.value)}
            >
              <MenuItem value="" disabled>
                {t('workflow.looptriggerflow.select_target_placeholder', 'Select a flow')}
              </MenuItem>
              {selectableFlows.map((flow) => (
                <MenuItem key={flow.id} value={String(flow.id)}>
                  {flow.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<AltRouteIcon />}
              label={t('workflow.looptriggerflow.loop_tag_chip', {
                defaultValue: 'Generated tag: {{tagName}}',
                tagName: normalizedLoopTagName || DEFAULT_LOOP_TAG_NAME
              })}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          <Typography sx={{ fontSize: '0.75rem', color: '#666', mt: 1.25, fontStyle: 'italic' }}>
            {t('workflow.looptriggerflow.help', {
              defaultValue: 'Each loop item replays the selected flow and exposes it through the {{tagToken}} tag. In project scope, the source tag is read from the project tags.',
              tagToken: `{${normalizedLoopTagName || DEFAULT_LOOP_TAG_NAME}}`
            })}
          </Typography>

          {selectableFlows.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.looptriggerflow.no_available_flows',
                'No other flow is available. Create another flow first.'
              )}
            </Alert>
          )}

          {isTargetMissing && selectableFlows.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.looptriggerflow.missing_target_warning',
                'Select a target flow before saving.'
              )}
            </Alert>
          )}

          {isLoopTagNameInvalid && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.looptriggerflow.loop_tag_name_invalid',
                'The temporary tag name must contain only letters, numbers or underscores.'
              )}
            </Alert>
          )}
        </CardContent>
      </Card>
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsLoopTriggerFlow);
